// @flow
import { EuroRounded, Percent } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, FormControl, FormHelperText, FormLabel, IconButton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ErrorMessage } from "formik";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { v4 as uuidv4 } from "uuid";
import FormBoxTitle from "../../../../../components/mui/app/formBoxTitle/formBoxTitle";
import { applyPercentage, calcItemTotalApplyingCharge, itemValue } from "../../../../../helpers/helpers";
import {
	formControlDefaults, numericFormatDefaults, numericFormatWithIconProps, textFieldDefaults,
} from "../../../../../helpers/theme/defaultProps";
import useUserData from "../../../../../hooks/user/useUserData";
import { ISSUES_FORM } from "../../../locales/namespaces";
import ItemAutoComplete from "../../form/partials/itemAutoComplete";

type Props = {};

const Items = (props: Props) => {
	const { t } = useTranslation(ISSUES_FORM)
	const { formik, initialItems, theme, IssueId, isBelowMd } = props
	const { thousandSeparator, decimalSeparator } = useUserData()
	
	const addItem = useCallback(() => {
		formik.setFieldValue('items', [
			...formik.values.items,
			{
				id: uuidv4(),
				ItemDescription: '',
				ItemId: '',
				Qty: 0,
				ItemPrice: 0,
				ChargeDescription: '',
				PCTCharge: 0,
				Note: ''
			}
		])
	}, [formik])
	
	const removeItem = useCallback((index) => {
		const updatedItems = [...formik.values.items]
		updatedItems.splice(index, 1)
		formik.setFieldValue('items', updatedItems)
	}, [formik])
	
	return (
		<Grid
			size={12}
		>
			<FormBoxTitle
				title={t('Items')}
				children={
					<Button
						onClick={addItem}
						variant={'text'}
						startIcon={<AddIcon/>}
					>
						{t('Add')}
					</Button>
				}
			/>
			{formik.values.items?.map((item, itemIndex) => {
				return (
					<Box key={item?.id} sx={{ borderLeft: `4px solid ${theme.palette.info.main}` }}>
						<Grid size={12}>
							<Box sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								background: '#fff5ee',
								paddingLeft: 0.4,
								paddingRight: 0.4
							}}>
								<FormControl {...formControlDefaults}
											 sx={{ display: 'inline' }}>
									<IconButton color="error"
												onClick={() => removeItem(itemIndex)}>
										<ClearIcon/>
									</IconButton>
								</FormControl>
							</Box>
						</Grid>
						<Grid
							container
							spacing={1}
							sx={{ padding: 0.4, marginBottom: 0.8 }}
						>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Item')}</FormLabel>
									<ItemAutoComplete
										IssueId={IssueId}
										itemIndex={itemIndex}
										items={initialItems}
										value={item?.ItemId}
										onChange={(evt, newValue, reason) => {
											if (reason === 'clear') {
												formik.setFieldValue(`items[${itemIndex}].ItemId`, '')
											} else {
												formik.setFieldValue(`items[${itemIndex}].ItemId`, newValue)
											}
										}}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage name={`items[${itemIndex}].ItemId`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Description')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={`items[${itemIndex}].ItemDescription`}
										value={item.ItemDescription}
										onChange={(e) => formik.setFieldValue(`items[${itemIndex}].ItemDescription`, e.target.value)}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage
											name={`items[${itemIndex}].ItemDescription`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Quantity')}</FormLabel>
									<NumericFormat
										{...numericFormatDefaults}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`items[${itemIndex}].Qty`}
										value={item.Qty}
										onValueChange={(values, sourceInfo) => {
											formik.setFieldValue(`items[${itemIndex}].Qty`, values.floatValue)
										}}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage
											name={`items[${itemIndex}].Qty`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Price')}</FormLabel>
									<NumericFormat
										{...numericFormatWithIconProps(EuroRounded)}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`items[${itemIndex}].ItemPrice`}
										value={item.ItemPrice}
										onValueChange={(values, sourceInfo) => {
											formik.setFieldValue(`items[${itemIndex}].ItemPrice`, values.floatValue)
										}}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage
											name={`items[${itemIndex}].ItemPrice`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Value')}</FormLabel>
									<NumericFormat
										{...numericFormatWithIconProps(EuroRounded)}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`items[${itemIndex}].ItemValue`}
										value={itemValue(item.Qty, item.ItemPrice)}
										disabled={true}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 'grow' }} sx={{maxWidth: isBelowMd ? '100%' : '150px', minWidth: '80px' }}>
								<FormControl
									{...formControlDefaults}
									error={
										formik.touched[`items[${itemIndex}].ChargeDescription`] &&
										Boolean(formik.errors[`items[${itemIndex}].ChargeDescription`])
									}
								>
									<FormLabel>{t('Surcharge')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={`items[${itemIndex}].ChargeDescription`}
										value={item.ChargeDescription}
										onChange={(e) => formik.setFieldValue(`items[${itemIndex}].ChargeDescription`, e.target.value)}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage name={`items[${itemIndex}].ChargeDescription`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl
									{...formControlDefaults}
									error={
										formik.touched[`items[${itemIndex}].PCTCharge`] &&
										Boolean(formik.errors[`items[${itemIndex}].PCTCharge`])
									}
								>
									<FormLabel>{t('Percentage')}</FormLabel>
									<NumericFormat
										{...numericFormatWithIconProps(Percent)}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`items[${itemIndex}].PCTCharge`}
										value={item.PCTCharge}
										onValueChange={(values, sourceInfo) => {
											formik.setFieldValue(`items[${itemIndex}].PCTCharge`, values.floatValue)
										}}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage name={`items[${itemIndex}].PCTCharge`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Total')}</FormLabel>
									<NumericFormat
										{...numericFormatWithIconProps(EuroRounded)}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`items[${itemIndex}].Total`}
										value={calcItemTotalApplyingCharge(item.Qty, item.ItemPrice, item.PCTCharge)}
										disabled={true}
									/>
								</FormControl>
							</Grid>
							<Grid size={12}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Note')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={`items[${itemIndex}].Note`}
										value={item.Note}
										onChange={(e) => formik.setFieldValue(`items[${itemIndex}].Note`, e.target.value)}
										onBlur={formik.handleBlur}
									/>
								</FormControl>
							</Grid>
						</Grid>
					</Box>
				)
			})}
			<FormHelperText error>
				{formik.errors?.items?.allItemsError}
			</FormHelperText>
		</Grid>
	);
};

export default Items